import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import WebsiteStepLinks from '@components/Publisher/Websites/Create/WebsiteStepLinks/WebsiteStepLinks';

import Textarea from '@uikit/old/Textarea/Textarea';
import { Button } from '@uikit/Form/Button/Button';
import Select from '@uikit/Form/Select/Select';
import { updatePublisherWebsiteApi } from '@api/publishers.api';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import { useHistory } from 'react-router-dom';
import {
  PublishersWebsiteDetailsType,
  PublisherWebsiteRestrictedNichesType,
} from '@ts/publishers.types';
import Loader from '@uikit/old/Loader/Loader';
import * as common_pb from 'respona_api/generated/common_pb';
import { PublisherWebsiteRestrictedNiches } from 'respona_api/generated/common_pb';
import Input from '@uikit/Form/Input/Input';
import { webSearchLanguages } from '@constants/languages.contstants';
import { orderItemVerticalToString } from '@utils/verticals';
import NumberInput from '@uikit/Form/NumberInput/NumberInput';

import './WebsiteStepThree.css';
import { websiteTypeToString } from '@utils/websiteTypes';

// Same limits as the server (PublisherWebsiteSettingsRules) and the CSV import: MIN 400..10000,
// MAX 800..10000, MIN below MAX. A form that allowed less would autosave values the server refuses.
const MIN_WORD_COUNT = 400;
const MIN_MAX_WORD_COUNT = 800;
const MAX_WORD_COUNT = 10000;
/** Summary value of a price whose switch is off on the price step (RES-7348). */
const PRICE_OFF_LABEL = 'Off';

function WebsiteStepThree({
  website,
  updateWebsite,
}: {
  website: PublishersWebsiteDetailsType;
  updateWebsite: (website: PublishersWebsiteDetailsType) => void;
}): JSX.Element {
  const history = useHistory();
  const currentWorkspaceId = useCurrentWorkspaceId();

  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState<string>('');
  const [restrictedNiches, setRestrictedNiches] = useState<PublisherWebsiteRestrictedNichesType[]>(
    []
  );
  const [turnaroundTime, setTurnaroundTime] = useState<number>(3);
  const [minWordCount, setMinWordCount] = useState<number>(800);
  const [maxWordCount, setMaxWordCount] = useState<number>(4000);
  const [typesError, setTypesError] = useState(false);
  const [types, setTypes] = useState<
    common_pb.PublisherWebsiteTypeMap[keyof common_pb.PublisherWebsiteTypeMap][]
  >([]);
  const [verticals, setVerticals] = useState<
    common_pb.OrderItemVerticalMap[keyof common_pb.OrderItemVerticalMap][]
  >([]);
  const [languages, setLanguages] = useState([]);

  const setStatesWithWebsiteFields = (fields) => {
    setNotes(fields.notes);
    setRestrictedNiches(fields.restrictedPricesList);
    setTurnaroundTime(fields.turnaroundTime);
    setLanguages(fields.languagesList);
    setTypes(fields.typesList);
    setVerticals(
      fields.verticalsList.length > 0
        ? fields.verticalsList
        : [common_pb.OrderItemVertical.UNDEFINED_VERTICAL]
    );
    setMinWordCount(fields.articleWordsCountMin);
    setMaxWordCount(fields.articleWordsCountMax);
  };

  useEffect(() => {
    if (website != null) {
      setStatesWithWebsiteFields(website);
      setIsLoading(false);
    }
  }, [website]);

  const toggleNiche = (niche: PublisherWebsiteRestrictedNichesType) => {
    setRestrictedNiches((prev) => {
      if (prev.find((it) => it.niche === niche.niche)) {
        return prev.filter((it) => it.niche !== niche.niche);
      }

      return [...prev, niche];
    });
  };

  useEffect(() => {
    if (
      !website ||
      isLoading ||
      turnaroundTime === -1 ||
      minWordCount < MIN_WORD_COUNT ||
      minWordCount >= maxWordCount ||
      maxWordCount > MAX_WORD_COUNT
    ) {
      return;
    }

    if (!isValidState()) {
      return;
    }

    const sameNotes = notes === website.notes;
    const restrictedEqual = _.isEqual(website.restrictedPricesList, restrictedNiches);

    const sameTurnaround = turnaroundTime === website.turnaroundTime;
    const websiteLanguages: string[] = Array.isArray(website.languagesList)
      ? (website.languagesList as string[])
      : [];
    const parsedLanguages = languages.map((l) => l.trim()).filter((l) => l.length > 0);
    const sameLanguages =
      websiteLanguages.length === parsedLanguages.length &&
      parsedLanguages.every((l) => websiteLanguages.includes(l));

    const sameTypes =
      types.length === website.typesList.length &&
      types.every((t) => website.typesList.includes(t));

    const websiteVerticals =
      website.verticalsList.length > 0
        ? website.verticalsList
        : [common_pb.OrderItemVertical.UNDEFINED_VERTICAL];
    const sameVerticals =
      verticals.length === websiteVerticals.length &&
      verticals.every((v) => websiteVerticals.includes(v));

    const sameMinWordCount = minWordCount === website.articleWordsCountMin;
    const sameMaxWordCount = maxWordCount === website.articleWordsCountMax;

    if (
      sameNotes &&
      restrictedEqual &&
      sameTurnaround &&
      sameLanguages &&
      sameTypes &&
      sameVerticals &&
      sameMinWordCount &&
      sameMaxWordCount
    ) {
      return;
    }

    const timer = setTimeout(() => {
      updatePublisherWebsiteApi(
        currentWorkspaceId,
        website.id,
        website.guestPostEnabled,
        website.guestPostLinkLimit,
        website.guestPostLinkPrice,
        website.linkInsertionEnabled,
        website.linkInsertionPrice,
        turnaroundTime,
        notes,
        types,
        verticals,
        restrictedNiches,
        parsedLanguages,
        minWordCount,
        maxWordCount
      ).then((response) => {
        setStatesWithWebsiteFields(response);
        updateWebsite(response);
      });
    }, 600);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [
    notes,
    restrictedNiches,
    website,
    currentWorkspaceId,
    updateWebsite,
    turnaroundTime,
    languages,
    types,
    verticals,
    minWordCount,
    maxWordCount,
  ]);

  const handleContinue = () => {
    if (!types || types.length === 0) {
      setTypesError(true);
      return;
    }

    setTypesError(false);

    const parsedLanguages = languages.map((l) => l.trim()).filter((l) => l.length > 0);
    updatePublisherWebsiteApi(
      currentWorkspaceId,
      website.id,
      website.guestPostEnabled,
      website.guestPostLinkLimit,
      website.guestPostLinkPrice,
      website.linkInsertionEnabled,
      website.linkInsertionPrice,
      turnaroundTime,
      notes,
      types,
      verticals,
      restrictedNiches,
      parsedLanguages,
      minWordCount,
      maxWordCount
    ).then((response) => {
      updateWebsite(response);
      redirectUserTo(history, `${urls.WEBSITE_STEP_FOUR(currentWorkspaceId, response.id)}`);
    });
  };

  const CATEGORIES = (): {
    label: string;
    value: common_pb.PublisherWebsiteRestrictedNichesMap[keyof common_pb.PublisherWebsiteRestrictedNichesMap];
  }[] => [
    { value: PublisherWebsiteRestrictedNiches.CBD_CANNABIS, label: 'CBD/Cannabis' },
    {
      value: PublisherWebsiteRestrictedNiches.PHARMA_MEDICINE,
      label: 'Pharma/Medicine',
    },
    { value: PublisherWebsiteRestrictedNiches.CRYPTO, label: 'Crypto' },
    { value: PublisherWebsiteRestrictedNiches.LOANS, label: 'Loans' },
    { value: PublisherWebsiteRestrictedNiches.GAMBLING_CASINO, label: 'Gambling/Casino' },
    { value: PublisherWebsiteRestrictedNiches.DATING, label: 'Dating' },
    { value: PublisherWebsiteRestrictedNiches.ADULT_CONTENT, label: 'Adult Content' },
    { value: PublisherWebsiteRestrictedNiches.TOBACCO, label: 'Tobacco' },
    { value: PublisherWebsiteRestrictedNiches.FIREARMS, label: 'Firearms' },
  ];

  if (!website) {
    return <Loader isFullScreenLoading />;
  }

  const TYPE_OPTIONS = Object.entries(common_pb.PublisherWebsiteType).map(([k, v]) => ({
    label: websiteTypeToString(v),
    value: v as common_pb.PublisherWebsiteTypeMap[keyof common_pb.PublisherWebsiteTypeMap],
  }));

  const VERTICAL_OPTIONS = Object.entries(common_pb.OrderItemVertical)
    .filter(([k, v]) => typeof v === 'number')
    .map(([k, v]) => ({
      label: orderItemVerticalToString(v as any),
      value: v as common_pb.OrderItemVerticalMap[keyof common_pb.OrderItemVerticalMap],
    }))
    .sort((a, b) => (a.value === common_pb.OrderItemVertical.UNDEFINED_VERTICAL ? -1 : 1));

  const LANGUAGES = webSearchLanguages;

  const goBack = () => {
    redirectUserTo(history, `${urls.WEBSITE_STEP_TWO(currentWorkspaceId, website.id)}`);
  };

  // Helpers: normalization and validation to ensure we only send valid, filled requests
  const normalizeLanguages = (vals: string[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    vals
      ?.map((l) => (typeof l === 'string' ? l.trim() : ''))
      .filter((l) => l.length > 0)
      .forEach((l) => {
        if (!seen.has(l)) {
          seen.add(l);
          out.push(l);
        }
      });
    return out;
  };

  const normalizeEnums = (vals: Array<number | undefined | null>): number[] => {
    return (vals ?? []).filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
  };

  type RestrictedItem = { niche: number; price: number };
  const normalizeRestricted = (vals: RestrictedItem[]): RestrictedItem[] => {
    const byNiche = new Map<number, RestrictedItem>();
    (vals ?? [])
      .filter((v): v is RestrictedItem => v != null && typeof v.niche === 'number')
      .forEach((v) => {
        const price =
          typeof v.price === 'number' && v.price > 0 ? v.price : website.guestPostPrice * 1.5;
        byNiche.set(v.niche, { niche: v.niche, price });
      });
    return Array.from(byNiche.values());
  };

  const numbersValid = (ta: number, minW: number, maxW: number): boolean => {
    const tOk = Number.isFinite(ta) && ta >= 1 && ta <= 10;
    const minOk = Number.isFinite(minW) && minW >= MIN_WORD_COUNT && minW <= MAX_WORD_COUNT;
    const maxOk = Number.isFinite(maxW) && maxW >= MIN_MAX_WORD_COUNT && maxW <= MAX_WORD_COUNT;
    const relOk = minW < maxW;
    return tOk && minOk && maxOk && relOk;
  };

  const buildPayload = () => {
    const langs = normalizeLanguages(languages as any);
    const tps = normalizeEnums(types as any);
    const verts = normalizeEnums(verticals as any);
    const niches = normalizeRestricted(restrictedNiches as any);
    return {
      langs,
      tps,
      verts,
      niches,
      ta: turnaroundTime,
      minW: minWordCount,
      maxW: maxWordCount,
      safeNotes: typeof notes === 'string' ? notes : '',
    };
  };

  const isValidState = (): boolean => {
    const { ta, minW, maxW } = buildPayload();
    // Non-numeric fields (languages/types/verticals/notes) are allowed to be empty per previous behavior.
    return numbersValid(ta, minW, maxW);
  };

  const defaultSelectStyles = {
    valueContainer: (provided) => ({ ...provided, padding: 4 }),
    multiValue: (provided) => ({
      ...provided,
      paddingTop: 1,
      paddingBottom: 2,
      backgroundColor: '#E9E9E9',
      borderRadius: 6,

      div: {
        maxWidth: '200px',
      },
    }),
    control: (provided) => ({
      ...provided,
      border: '1px solid #DBDFE7',
      borderRadius: '8px',
      minHeight: '36px',
      height: '36px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      marginRight: 7,
      marginleft: 3,
      cursor: 'pointer',

      '&:hover': {
        backgroundColor: 'transparent',
      },
      svg: {
        fill: '#fff',
        stroke: '#fff',
        backgroundColor: '#ED483D',
        borderRadius: '50%',
      },
    }),
  };

  return (
    <div className="website-step--step-three" data-testid="website-wizard-step-3">
      <WebsiteStepLinks id={website.id} isDisabledStep={false} step={3} />
      <div className="website-step-container wide-content">
        <h2 className="website-step-container__title">Additional details</h2>

        {/* Turnaround time */}
        <div className="website-step-container__block website-step-container__block--top">
          <div className="website-step-container__header">
            <p className="title">
              <span className="logo">
                <img src={website.logo} alt="" />
              </span>
              {website.domain}
            </p>
            <Button
              className="edit-btn"
              type="ghost"
              iconSize={12}
              leftIcon="pencil"
              colorIcon="#251FB6"
              size="s"
              onClick={goBack}
              data-testid="website-wizard-back"
            >
              Edit
            </Button>
          </div>
          <div className="website-step-container__content flex-content">
            <div className="website-step-container__column">
              <h4
                data-testid="website-wizard-summary-guest-post-price"
                data-enabled={website.guestPostEnabled ? 'true' : 'false'}
              >
                {website.guestPostEnabled
                  ? `$${Math.round(website.guestPostLinkPrice * website.guestPostLinkLimit)}`
                  : PRICE_OFF_LABEL}
              </h4>
              <p>guest post price</p>
            </div>

            <div className="website-step-container__column">
              <h4
                data-testid="website-wizard-summary-link-insertion-price"
                data-enabled={website.linkInsertionEnabled ? 'true' : 'false'}
              >
                {website.linkInsertionEnabled ? `$${website.linkInsertionPrice}` : PRICE_OFF_LABEL}
              </h4>
              <p>Link insertion price</p>
            </div>

            <div className="website-step-container__column">
              <h4 data-testid="website-wizard-summary-link-limit">
                {website.guestPostEnabled ? website.guestPostLinkLimit : '—'}
              </h4>
              <p>Max # of dofollow links per guest post</p>
            </div>
          </div>
        </div>

        <div className="website-step-container__block">
          <p className="website-step-container__label">Accepted topics (select all that apply)</p>
          <div className="website-step-container__content">
            <div className="website-step-container__item pt-8">
              <Select
                isMulti
                testId="website-wizard-topics"
                visibleMultiCount={10}
                value={
                  verticals.length === 0 ||
                  verticals.includes(common_pb.OrderItemVertical.UNDEFINED_VERTICAL)
                    ? VERTICAL_OPTIONS.filter(
                        (o) => o.value === common_pb.OrderItemVertical.UNDEFINED_VERTICAL
                      )
                    : VERTICAL_OPTIONS.filter((o) => verticals.includes(o.value))
                }
                options={VERTICAL_OPTIONS}
                onChange={(vals: any[]) => {
                  const newValues = normalizeEnums((vals ?? []).map((v: any) => v?.value));
                  const hasAny = newValues.includes(common_pb.OrderItemVertical.UNDEFINED_VERTICAL);
                  const hadAny =
                    verticals.length === 0 ||
                    verticals.includes(common_pb.OrderItemVertical.UNDEFINED_VERTICAL);

                  if (hasAny && !hadAny) {
                    setVerticals([common_pb.OrderItemVertical.UNDEFINED_VERTICAL]);
                  } else {
                    const filtered = newValues.filter(
                      (v) => v !== common_pb.OrderItemVertical.UNDEFINED_VERTICAL
                    );
                    setVerticals(
                      filtered.length === 0
                        ? [common_pb.OrderItemVertical.UNDEFINED_VERTICAL]
                        : (filtered as any)
                    );
                  }
                }}
                additionalStyles={{
                  ...defaultSelectStyles,
                  control: (provided) => ({
                    ...provided,
                    alignItems: 'start',
                    height: '122px !important',
                    borderRadius: 8,
                    borderColor: '#DBDFE7',
                  }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    position: 'absolute',
                    bottom: 0,
                    left: 'calc(50% - 17px)',
                  }),
                }}
                isClearable={false}
              />
            </div>
          </div>
        </div>

        <div className="website-step-container__block--two-columns">
          <div className="website-step-container__block--two-columns__left">
            <div className="website-step-container__block mb-0">
              <p className="website-step-container__label">Website type *</p>
              <div className="website-step-container__content">
                <div
                  className="website-step-container__item pt-8"
                  data-testid="website-wizard-website-type-field"
                  data-invalid={typesError ? 'true' : 'false'}
                >
                  <Select
                    isMulti
                    testId="website-wizard-website-type"
                    isClearable={false}
                    visibleMultiCount={5}
                    value={TYPE_OPTIONS.filter((o) => types.includes(o.value))}
                    options={TYPE_OPTIONS}
                    onChange={(vals: any[]) => {
                      setTypes(normalizeEnums((vals ?? []).map((v: any) => v?.value)) as any);
                      setTypesError(false);
                    }}
                    additionalStyles={{
                      ...defaultSelectStyles,
                      control: (base) => ({
                        ...base,
                        borderColor: typesError ? '#ED483D' : base.borderColor,
                      }),
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="website-step-container__block mb-0">
              <p className="website-step-container__label">Accepted languages</p>
              <div className="website-step-container__content">
                <div className="website-step-container__item pt-8">
                  <Select
                    isMulti
                    testId="website-wizard-languages"
                    isClearable={false}
                    visibleMultiCount={5}
                    value={
                      languages.length === 0
                        ? LANGUAGES.filter((o) => o.value === '')
                        : LANGUAGES.filter((o) => languages.includes(o.value.split('_')[1]))
                    }
                    options={LANGUAGES}
                    onChange={(vals: any[]) => {
                      const newValues = (vals ?? []).map((v: any) =>
                        v?.value?.includes('_') ? v.value.split('_')[1] : v.value
                      );

                      const hasAnyLanguage = newValues.some((v) => v === '');
                      const hadAnyLanguage = languages.length === 0;

                      if (hasAnyLanguage && !hadAnyLanguage) {
                        setLanguages([]);
                      } else {
                        setLanguages(normalizeLanguages(newValues) as any);
                      }
                    }}
                    additionalStyles={defaultSelectStyles}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="website-step-container__block--two-columns__right">
            <div className="website-step-container__block mb-0">
              <p className="website-step-container__label">Turnaround time</p>
              <div className="website-step-container__content">
                <div className="website-step-container__item pt-8">
                  <label className="website-step-container__turnaround-time-label">day(s)</label>
                  <Input
                    className="website-step-container__input-number"
                    data-testid="website-wizard-turnaround"
                    type="number"
                    min={1}
                    max={10}
                    value={turnaroundTime === -1 ? '' : turnaroundTime}
                    onChange={(e) => {
                      const val = (e.target as HTMLInputElement).value;
                      if (val === '') {
                        setTurnaroundTime(-1);
                        return;
                      }
                      const num = parseInt(val, 10);
                      const clamped = Math.max(1, Math.min(10, num));
                      setTurnaroundTime(clamped);
                    }}
                    onBlur={() => {
                      if (turnaroundTime === -1) {
                        setTurnaroundTime(1);
                      }
                    }}
                    placeholder="Time"
                  />
                </div>
              </div>
            </div>
            <div className="website-step-container__block mb-0">
              <p className="website-step-container__label">Word count</p>
              <div className="website-step-container__content">
                <div className="website-step-container__item grid-column-none pt-8">
                  <div className="website-step-container__input">
                    <label>Min</label>
                    <input
                      type="number"
                      data-testid="website-wizard-words-min"
                      min={MIN_WORD_COUNT}
                      max={MAX_WORD_COUNT}
                      value={minWordCount === -1 ? '' : minWordCount}
                      onChange={(e) => {
                        const val = (e.target as HTMLInputElement).value;
                        if (val === '') {
                          setMinWordCount(-1);
                          return;
                        }
                        const num = parseInt(val, 10);
                        setMinWordCount(Math.min(num, maxWordCount - 1));
                      }}
                      onBlur={() => {
                        const clamped = Math.max(
                          MIN_WORD_COUNT,
                          Math.min(maxWordCount - 1, minWordCount)
                        );
                        setMinWordCount(clamped);
                      }}
                      style={{ width: 100 }}
                    />
                  </div>
                  <div className="website-step-container__input">
                    <label>Max</label>
                    <input
                      type="number"
                      data-testid="website-wizard-words-max"
                      min={MIN_MAX_WORD_COUNT}
                      max={MAX_WORD_COUNT}
                      value={maxWordCount === -1 ? '' : maxWordCount}
                      onChange={(e) => {
                        const val = (e.target as HTMLInputElement).value;
                        if (val === '') {
                          setMaxWordCount(-1);
                          return;
                        }
                        const num = parseInt(val, 10);
                        setMaxWordCount(num);
                      }}
                      onBlur={() => {
                        const clamped = Math.min(
                          MAX_WORD_COUNT,
                          Math.max(maxWordCount, minWordCount + 1, MIN_MAX_WORD_COUNT)
                        );
                        setMaxWordCount(clamped);
                      }}
                      style={{ width: 100 }}
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="website-step-container__block mb-0"> */}
            {/*  <p className="website-step-container__label website-step-container__label-category"> */}
            {/*    Special category guest post pricing */}
            {/*  </p> */}
            {/*  <div className="website-step-container__content"> */}
            {/*    {CATEGORIES().map(({ label, value }) => { */}
            {/*      const niche = restrictedNiches.find((it) => it.niche === value); */}

            {/*      return ( */}
            {/*        <div className="website-step-container__item pt-8" key={label}> */}
            {/*          <SlideToggler */}
            {/*            value={!!niche} */}
            {/*            onChange={() => */}
            {/*              toggleNiche({ */}
            {/*                niche: value, */}
            {/*                price: website.guestPostPrice, */}
            {/*              }) */}
            {/*            } */}
            {/*          /> */}
            {/*          <p>{label}</p> */}
            {/*          {niche ? ( */}
            {/*            <div className="website-step-container__number-input"> */}
            {/*              <NumberInput */}
            {/*                disabledArrows */}
            {/*                value={niche.price} */}
            {/*                maxNumber={9999} */}
            {/*                minNumber={0} */}
            {/*                onChange={(e) => { */}
            {/*                  setRestrictedNiches( */}
            {/*                    restrictedNiches.map((it) => { */}
            {/*                      if (it.niche === niche.niche) { */}
            {/*                        return { */}
            {/*                          ...it, */}
            {/*                          price: parseInt(e.toString(), 10) || 0, */}
            {/*                        }; */}
            {/*                      } */}
            {/*                      return it; */}
            {/*                    }) */}
            {/*                  ); */}
            {/*                }} */}
            {/*              /> */}
            {/*            </div> */}
            {/*          ) : null} */}
            {/*        </div> */}
            {/*      ); */}
            {/*    })} */}
            {/*  </div> */}
            {/* </div> */}
          </div>
        </div>

        <div className="website-step-container__textarea">
          <label htmlFor="textarea">Custom guidelines (not guaranteed)</label>
          <Textarea
            id="textarea"
            data-testid="website-wizard-guidelines"
            placeholder="You can enter your general content guidelines (if applicable)."
            resizeDisabled
            value={notes}
            onChange={(e: any) => setNotes((e?.target?.value ?? '').toString())}
            maxLength={10000}
          />
        </div>

        <Button
          className="website-step-container__button"
          onClick={handleContinue}
          disabled={!isValidState()}
          data-testid="website-wizard-next"
        >
          Continue
        </Button>
      </div>
      <div className="website-step-container__dammy" />
    </div>
  );
}

export default WebsiteStepThree;
